import { type MouseEvent as ReactMouseEvent, useEffect, useRef, useState } from "react";

interface DragState {
  startX: number;
  startY: number;
  originX: number;
  originY: number;
  minY: number;
  paperElement: Element | null;
}

export function useDraggableDialog(props: { open: boolean; visibleHeaderSize?: number }) {
  const { open, visibleHeaderSize = 80 } = props;
  const [dialogOffset, setDialogOffset] = useState({ x: 0, y: 0 });
  const dragStateRef = useRef<DragState | null>(null);
  const onMouseMoveRef = useRef<(event: MouseEvent) => void>();
  const onMouseUpRef = useRef<() => void>();

  if (!onMouseMoveRef.current) {
    onMouseMoveRef.current = (event: MouseEvent) => {
      const dragState = dragStateRef.current;
      if (!dragState) {
        return;
      }

      const nextX = dragState.originX + event.clientX - dragState.startX;
      const nextY = dragState.originY + event.clientY - dragState.startY;
      if (!dragState.paperElement) {
        setDialogOffset({
          x: nextX,
          y: Math.max(dragState.minY, nextY),
        });
        return;
      }

      const paperRect = dragState.paperElement.getBoundingClientRect();
      setDialogOffset((currentOffset) => {
        const deltaX = nextX - currentOffset.x;
        const deltaY = nextY - currentOffset.y;

        const rawLeft = paperRect.left + deltaX;
        const minLeft = -paperRect.width + visibleHeaderSize;
        const maxLeft = window.innerWidth - visibleHeaderSize;
        const clampedLeft = Math.min(maxLeft, Math.max(minLeft, rawLeft));
        const clampedX = currentOffset.x + (clampedLeft - paperRect.left);

        const rawTop = paperRect.top + deltaY;
        const minTop = paperRect.top + (dragState.minY - currentOffset.y);
        const maxTop = window.innerHeight - visibleHeaderSize;
        const clampedTop = Math.min(maxTop, Math.max(minTop, rawTop));
        const clampedY = currentOffset.y + (clampedTop - paperRect.top);

        return {
          x: clampedX,
          y: clampedY,
        };
      });
    };
  }

  if (!onMouseUpRef.current) {
    onMouseUpRef.current = () => {
      dragStateRef.current = null;
      window.removeEventListener("mousemove", onMouseMoveRef.current!);
      window.removeEventListener("mouseup", onMouseUpRef.current!);
    };
  }

  useEffect(() => {
    if (!open) {
      setDialogOffset({ x: 0, y: 0 });
    }
  }, [open]);

  useEffect(() => {
    return () => {
      window.removeEventListener("mousemove", onMouseMoveRef.current!);
      window.removeEventListener("mouseup", onMouseUpRef.current!);
    };
  }, []);

  const onTitleMouseDown = (event: ReactMouseEvent) => {
    if ((event.target as HTMLElement).closest('[data-testid="dialog-close-button"]')) {
      return;
    }

    const paperElement = (event.currentTarget as HTMLElement).closest(".MuiDialog-paper");
    const paperTop = paperElement?.getBoundingClientRect().top ?? 0;

    dragStateRef.current = {
      startX: event.clientX,
      startY: event.clientY,
      originX: dialogOffset.x,
      originY: dialogOffset.y,
      minY: dialogOffset.y + 8 - paperTop,
      paperElement,
    };
    window.addEventListener("mousemove", onMouseMoveRef.current!);
    window.addEventListener("mouseup", onMouseUpRef.current!);
  };

  return {
    dialogOffset,
    onTitleMouseDown,
  };
}
